<template>
  <component
    :is="wholeBlockLink ? 'a' : 'aside'"
    class="view-citation"
    :class="{ 'is-reference': !texts.length, 'is-footer': footer, 'is-clickable': wholeBlockLink }"
    :href="wholeBlockLink ? sourceHref : null"
    :target="wholeBlockLink && destination.href ? '_blank' : null"
    :rel="wholeBlockLink && destination.href ? 'noopener' : null"
    :draggable="wholeBlockLink ? false : null"
    :aria-label="wholeBlockLink ? `Read ${source.title} in ${pageLabel}` : `Cited from ${pageLabel}`"
    @pointerdown="rememberSelection"
    @click="openCitation"
  >
    <blockquote v-if="texts.length">
      <p v-for="(text, index) in texts" :key="`${index}-${text}`">{{ text }}</p>
    </blockquote>
    <component :is="wholeBlockLink ? 'span' : 'button'" :type="wholeBlockLink ? null : 'button'" class="citation-source" @click="openFooter">
      <span class="citation-mark" aria-hidden="true">↳</span>
      <span class="citation-copy">
        <span class="citation-page">Cited from {{ pageLabel }}</span>
        <strong>{{ source.title }}</strong>
      </span>
      <ArrowUpRightIcon class="citation-open" />
    </component>
  </component>
</template>

<script>
import { sourcePageLabel, viewSourceTarget } from '../../utils/viewSourceTarget';

export default {
  name: 'ViewCitation',
  props: {
    source: { type: Object, required: true },
    texts: { type: Array, default: () => [] },
    footer: { type: Boolean, default: false },
  },
  data() {
    return { selectionAtPointerDown: '' };
  },
  computed: {
    destination() {
      return this.footer ? null : viewSourceTarget(this.source);
    },
    wholeBlockLink() {
      return Boolean(this.destination);
    },
    sourceHref() {
      if (!this.destination) return null;
      return this.destination.href || this.$router.resolve(this.destination).href;
    },
    pageLabel() {
      return sourcePageLabel(this.source);
    },
  },
  emits: ['open-source'],
  methods: {
    rememberSelection() {
      if (this.wholeBlockLink) this.selectionAtPointerDown = window.getSelection()?.toString() || '';
    },
    openFooter() {
      if (!this.wholeBlockLink) this.$emit('open-source', this.source);
    },
    openCitation(event) {
      if (!this.wholeBlockLink || event.defaultPrevented) return;
      // Keep native new-tab actions and allow visitors to select quoted text.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const selection = window.getSelection();
      if (event.detail !== 0 && selection && !selection.isCollapsed && selection.toString() !== this.selectionAtPointerDown && (
        event.currentTarget.contains(selection.anchorNode) || event.currentTarget.contains(selection.focusNode)
      )) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      this.$emit('open-source', this.source);
    },
  },
};
</script>

<style scoped>
.view-citation {
  display: block;
  margin: 1.15rem 0;
  padding: 1rem 1.05rem 0.9rem;
  border: 0;
  background: rgba(var(--site-accent-rgb), 0.08);
}

.view-citation.is-clickable {
  color: inherit;
  text-decoration: none;
  cursor: var(--site-interactive-cursor);
  user-select: text;
  -webkit-user-select: text;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.is-clickable .citation-source:active { transform: none; background: transparent; }

blockquote {
  margin: 0 0 0.85rem;
  padding: 0;
  border: 0;
}

blockquote p {
  margin: 0.5rem 0;
  color: #464646;
  font-size: 0.98rem;
  line-height: 1.7;
  text-wrap: pretty;
}

.citation-source {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  min-height: 2.5rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.citation-copy,
.citation-page,
.citation-copy strong {
  display: block;
}

.citation-page {
  margin-bottom: 0.15rem;
  color: var(--site-accent);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}

.citation-copy strong {
  overflow: hidden;
  font-size: 0.83rem;
  font-weight: 600;
  line-height: 1.28;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.citation-mark,
.citation-open {
  color: var(--site-accent);
  font-family: "Quattrocento Sans", sans-serif;
}

.citation-open {
  transition: transform 160ms ease;
}

.is-clickable:focus-visible .citation-open,
.citation-source:focus-visible .citation-open {
  transform: translate(2px, -2px);
}

@media (hover: hover) {
  .is-clickable:hover .citation-open,
  .citation-source:hover .citation-open { transform: translate(2px, -2px); }
}

.is-clickable:focus-visible,
.citation-source:focus-visible {
  outline: 1px solid var(--site-accent);
  outline-offset: 4px;
}

.citation-source:active {
  transform: scale(0.995);
}

.is-reference {
  display: inline-block;
  max-width: min(100%, 28rem);
  margin: 0.7rem 0 0;
  padding: 0.5rem 0.7rem;
}

.is-reference .citation-source {
  min-height: 1.8rem;
}

.is-footer {
  display: block;
  width: auto;
  max-width: none;
  margin: 0;
  padding: 0.62rem 0.85rem;
  border: 0;
  background: rgba(var(--site-accent-rgb), 0.09);
}

.is-footer .citation-source {
  min-height: 2rem;
}

body.dark-mode .view-citation {
  background: rgba(var(--site-accent-rgb), 0.1);
}

body.dark-mode blockquote p {
  color: #c9c9c9;
}

body.dark-mode .is-footer {
  background: rgba(var(--site-accent-rgb), 0.1);
}

@media (prefers-reduced-motion: reduce) {
  .citation-open { transition: none; }
  .is-clickable:hover .citation-open,
  .is-clickable:focus-visible .citation-open,
  .citation-source:hover .citation-open,
  .citation-source:focus-visible .citation-open { transform: none; }
}
</style>
